/* function to build the url of an image, adding the url before the image path */
export const getImageUrl = (url: string | null | undefined): string | null => {
  if (url == null) return null;
  return `${process.env.NEXT_PUBLIC_URI}${encodeImagePath(url)}`;
};

/**
 * Encode les caractères problématiques d'un chemin d'image (espaces, accents, etc.)
 * sans toucher aux séparateurs / ni au query string. Évite les srcset cassés.
 */
export const encodeImagePath = (path: string): string => {
  if (!path) return path;
  const [pathPart, queryPart] = path.split('?');
  const encoded = pathPart.split('/').map((seg) => encodeURIComponent(seg).replace(/%2F/gi, '/')).join('/');
  return queryPart ? `${encoded}?${queryPart}` : encoded;
};

/**
 * Loader Next/Image partagé : préfixe avec NEXT_PUBLIC_URI et encode le chemin.
 */
export const buildImageLoaderUrl = (src: string, width: number, quality?: number): string => {
  const encoded = encodeImagePath(src);
  return `${process.env.NEXT_PUBLIC_URI}${encoded}?w=${width}&q=${quality || 75}`;
};

interface Entry {
  collection?: {
    code?: string;
  };
}

export const entriesHasElementWithCode = (entries: Entry[] | null | undefined, code: string): boolean => {
  let hasEntry = false;
  if (entries) {
    entries.map((entry) => {
      if (entry && entry.collection && entry.collection.code === code) {
        hasEntry = true;
      }
      return hasEntry;
    });
  }

  return hasEntry;
};

interface RRuleLanguage {
  dayNames: string[];
  monthNames: string[];
}

interface RRule {
  toText: (
    getText?: (id: string | number | { toString(): string }) => string | undefined,
    language?: RRuleLanguage,
    dateFormat?: (year: string | number, month: string | number, day: string | number) => string
  ) => string;
}

export const rruleToText = (rrule: RRule): string => {
  const yourStrings: Record<string, string> = {
    every: 'Chaque',
    until: "jusqu'au",
    day: 'jour',
    days: 'jour',
    week: 'semaine',
    weeks: 'semaines',
    on: 'le',
    in: 'in',
    'on the': 'le',
    for: 'pour',
    and: 'et',
    or: 'ou',
    at: 'à',
    last: 'dernier',
    '(~ approximate)': '(~ approximativement)',
    times: 'fois',
    time: 'fois',
    minutes: 'minutes',
    hours: 'heures',
    weekdays: 'jours de la semaine',
    weekday: 'jour de la semaine',
    months: 'mois',
    month: 'mois',
    years: 'années',
    year: 'année',
  };
  const language: RRuleLanguage = {
    dayNames: [
      'dimanche',
      'lundi',
      'mardi',
      'mercredi',
      'jeudi',
      'vendredi',
      'samedi',
    ],
    monthNames: [
      'janvier ',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'aoùt',
      'septembre',
      'octobre',
      'novembre',
      'décembre',
    ],
  };
  const getText = (id: string | number | { toString(): string }): string | undefined => {
    return yourStrings[String(id)];
  };

  const dateFormat = (year: string | number, month: string | number, day: string | number): string => `${day} ${month} ${year}`;

  return rrule.toText(getText, language, dateFormat);
};

export const urlWithHttpsdefault = (url: string | null | undefined): string | null | undefined => {
  if (url && !url.includes('http')) {
    return `https://${url}`;
  }
  return url;
};

export const urlRectification = (text: string): string => {
  const urlRegex = /([\w+]+\:\/\/)?([\w\d-]+\.)*[\w-]+[\.\:]\w+([\/\?\=\&\#\.]?[\w-]+)*\/?/gi;
  return text.replace(urlRegex, (url) => {
    return `${urlWithHttpsdefault(url)}`;
  });
};

/* parse Text and find the URL to show as hyperlink */
export const linkify = (text: string): string => {
  const urlRegex = /([\w+]+\:\/\/)?([\w\d-]+\.)*[\w-]+[\.\:]\w+([\/\?\=\&\#\.]?[\w-]+)*\/?/gi;
  return text.replace(urlRegex, (url) => {
    return `<a href="${urlWithHttpsdefault(url)}" + target="_blank">${url}</a>`;
  });
};
